import { useContext, useEffect, useMemo, useState } from 'react';
import {
  AgxColumn,
  AgxRow,
  AgxHeader,
  AgxSelect,
  Option,
} from '@urbanx/agx-ui-components';
import {
  AgxEntityStatistics,
  AgxPartnershipIndicator,
  AgxButton,
  AgxModal,
  AgxTextInput,
  AgxDropdown,
  useWindowSize,
  Breakpoints,
  AgxDateRangeSelector,
  AgxSpacer,
} from '@urbanx/agx.ui-components2';
import { useAppSelector } from 'hooks/useAppSelector';
import './reportingDashboard.scss';
import { ReportingDashboardState } from './reportingDashboardReducer';
import Leaderboard from './Leaderboard/Leaderboard';
import UpcomingSettlements from './UpcomingSettlements/UpcomingSettlements';
import useGoals from './Hooks/useGoals';
import GoalsModal from './GoalsModal/GoalsModal';
import { User } from 'types/Auth';
import { useLoadAgencyAgents } from './Hooks/useLoadAgencyAgents';
import {
  useInvalidateLoadRexStatsByTypeByDate,
  useLoadRexStatsByTypeByDate,
} from './Hooks/useLoadRexStatsByTypeByDate';
import { useLoadLeaderboardStats } from './Hooks/useLeaderboardStatistics';
import { useLoadPartnershipInfo } from './Hooks/useLoadPartnershipInfo';
import { SaleGoals } from 'Api/Agencies/Types/Agencies';
import { AgentRole, AuthContext } from '@urbanx/agx.auth.client';
import {
  PartnershipDetailsPartnershipLevel,
  StatisticsForEntityRequest,
  ContractDateToUse,
  StatisticsForEntityRequestEntityType,
  StatisticsForEntityRequestTimePeriod,
} from '@urbanx/agx.rex.client';
import dayjs from 'dayjs';

const PartnershipYearOptions = [
  {
    value: 'This Year',
    label: 'This Year',
  },
  {
    value: 'Last Year',
    label: 'Last Year',
  },
];

const TimePeriods: Option[] = [
  {
    value: StatisticsForEntityRequestTimePeriod.Month.toString(),
    label: 'Month',
  },
  {
    value: StatisticsForEntityRequestTimePeriod.Quarter.toString(),
    label: 'Quarter',
  },
  {
    value: StatisticsForEntityRequestTimePeriod.Annual.toString(),
    label: 'Year',
  },
  {
    value: StatisticsForEntityRequestTimePeriod.Previous.toString(),
    label: 'Last 12 months',
  },
  {
    value: StatisticsForEntityRequestTimePeriod.Custom.toString(),
    label: 'Custom',
  },
];

const contractTypeOptions: Option[] = [
  {
    value: ContractDateToUse.Unconditional.toString(),
    label: 'Unconditional',
  },
  {
    value: ContractDateToUse.Written.toString(),
    label: 'Written',
  },
  {
    value: ContractDateToUse.Settled.toString(),
    label: 'Settled',
  },
];

const findUserInAgents = (agents: AgentDetails[], user?: User) => {
  if (!user || !user.agent) return undefined;
  if (agents.length === 0) return undefined;

  const val = agents.find(agent => agent.value === user.agent) ?? agents[0];

  return val;
};

interface AgentDetails {
  label: string;
  value: string;
}

const ReportingDashboard = () => {
  const [selectedFirstDate, setSelectedFirstDate] = useState('');
  const [selectedSecondDate, setSelectedSecondDate] = useState('');
  const agencyGoals = useAppSelector(
    (state: { reportingDashboard: ReportingDashboardState }) =>
      state.reportingDashboard.goals?.agencyGoals
  );

  const { userAccount } = useContext(AuthContext);

  const { agencyInfo } = useAppSelector(state => state.agencies);
  const agencyName = agencyInfo?.info?.branding?.agencyName || 'Agency';

  const entityTypeOptions = useMemo(() => {
    const { roles } = userAccount || {};

    const hasDirectorRole = roles?.includes(AgentRole.Director);

    const defaultOptions: Option[] = [
      {
        value: StatisticsForEntityRequestEntityType.IndividualOrTeam.toString(),
        label: 'Individual/Team',
      },
    ];

    const directorOptions: Option[] = [
      {
        value: StatisticsForEntityRequestEntityType.Agency.toString(),
        label: 'Agency',
      },
      {
        value: StatisticsForEntityRequestEntityType.Partnership.toString(),
        label: 'Partnership',
      },
    ];

    let options = [...defaultOptions];

    if (hasDirectorRole) {
      options = [...options, ...directorOptions];
    }

    return options;
  }, [userAccount]);

  const [contractTypeOption, setContractTypeOption] = useState(
    contractTypeOptions[0]
  );
  const [entityType, setEntityType] = useState(entityTypeOptions[0]);
  const [timePeriod, setTimePeriod] = useState(TimePeriods[0]);

  const [partnershipYearPeriod, setPartnershipYearPeriod] = useState(
    PartnershipYearOptions[0]
  );

  const isThisYear =
    partnershipYearPeriod.value === PartnershipYearOptions[0].value;

  const {
    partnershipInfo,
    isPending: partnershipStatsPending,
    isError: partnershipStatsError,
  } = useLoadPartnershipInfo(true, isThisYear);

  const { agents: listOfAgents, isPending: isAgentsInAgencyPending } =
    useLoadAgencyAgents();

  const agents: AgentDetails[] = useMemo(() => {
    const mapped =
      listOfAgents?.map(agent => ({
        label: `${agent?.name?.firstName} ${agent?.name?.lastName}`,
        value: agent.id,
      })) ?? [];
    mapped.sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
    return mapped;
  }, [listOfAgents]);

  const [selectedAgent, setSelectedAgent] = useState(
    findUserInAgents(agents, userAccount)
  );

  const [tempEntityType, setTempEntityType] = useState(entityType);
  const [tempSelectedAgent, setTempSelectedAgent] = useState(
    findUserInAgents(agents, userAccount)
  );
  const [tempContractTypeOption, setTempContractTypeOption] =
    useState(contractTypeOption);
  const [tempTimePeriod, setTempTimePeriod] = useState(timePeriod);
  const [tempStartDate, setTempStartDate] = useState(selectedFirstDate);
  const [tempEndDate, setTempEndDate] = useState(selectedSecondDate);

  useEffect(() => {
    if (selectedAgent) return;

    setSelectedAgent(findUserInAgents(agents, userAccount));
  }, [agents]);

  const {
    leaderboardStatsResponse: leaderboardStatistics,
    isPending: leaderboardPending,
  } = useLoadLeaderboardStats(
    contractTypeOption.value as unknown as ContractDateToUse,
    selectedFirstDate,
    selectedSecondDate,
    entityType.value as unknown as StatisticsForEntityRequestEntityType,
    timePeriod.value as unknown as StatisticsForEntityRequestTimePeriod,
    userAccount?.roles?.includes(AgentRole.Director)
      ? selectedAgent?.value
      : undefined
  );

  const [goals, saveGoals] = useGoals(
    entityType.value as StatisticsForEntityRequestEntityType
  );

  const windowSize = useWindowSize();
  const isMobileSize = windowSize === Breakpoints.Mobile;

  const showPartnershipInfo =
    entityType.value ===
    StatisticsForEntityRequestEntityType.Partnership.toString();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    if (isFilterOpen) {
      setTempEntityType(entityType);
      setTempSelectedAgent(selectedAgent);
      setTempContractTypeOption(contractTypeOption);
      setTempTimePeriod(timePeriod);
      setTempStartDate(selectedFirstDate);
      setTempEndDate(selectedSecondDate);
    }
  }, [isFilterOpen]);

  useEffect(() => {
    const today = new Date();
    let dateToChange = new Date(today);
    let firstDateString = '';

    switch (timePeriod.value) {
      case StatisticsForEntityRequestTimePeriod.Month.toString():
        dateToChange.setMonth(dateToChange.getMonth() - 1);
        break;
      case StatisticsForEntityRequestTimePeriod.Quarter.toString():
        dateToChange.setMonth(dateToChange.getMonth() - 3);
        break;
      case StatisticsForEntityRequestTimePeriod.Annual.toString():
        firstDateString = `${today.getFullYear()}-01-01`;
        break;
      case StatisticsForEntityRequestTimePeriod.Previous.toString():
        dateToChange.setFullYear(dateToChange.getFullYear() - 1);
        break;
      case StatisticsForEntityRequestTimePeriod.Custom.toString():
        dateToChange = new Date(selectedFirstDate);
        break;
      default:
        dateToChange = new Date();
    }

    const formattedStartDate =
      timePeriod.value === 'Year'
        ? firstDateString
        : dateToChange.toISOString().split('T')[0];

    const formattedEndDate = today.toISOString().split('T')[0];

    setSelectedFirstDate(formattedStartDate);
    setSelectedSecondDate(formattedEndDate);

    // Setting temporary dates for custom date range
    setTempStartDate(formattedStartDate);
    setTempEndDate(formattedEndDate);
  }, [timePeriod]);

  const updatedFilters: StatisticsForEntityRequest = useMemo(() => {
    const currentEntityType =
      entityType.value as unknown as StatisticsForEntityRequestEntityType;
    let updatedTimePeriod =
      timePeriod.value as unknown as StatisticsForEntityRequestTimePeriod;
    let updatedContractDateToUse =
      contractTypeOption.value as unknown as ContractDateToUse;

    if (
      currentEntityType === StatisticsForEntityRequestEntityType.Partnership
    ) {
      if (partnershipYearPeriod.value === PartnershipYearOptions[0].value) {
        updatedTimePeriod =
          StatisticsForEntityRequestTimePeriod.ThisPartnershipYear;
      } else {
        updatedTimePeriod =
          StatisticsForEntityRequestTimePeriod.LastPartnershipYear;
      }

      updatedContractDateToUse = ContractDateToUse.Settled;
    }

    return {
      entityType: currentEntityType,
      selectedAgent: userAccount?.roles?.includes(AgentRole.Director)
        ? selectedAgent?.value
        : undefined,
      contractDateToUse: updatedContractDateToUse,
      timePeriod: updatedTimePeriod,
      fromDate: dayjs(selectedFirstDate).toDate(),
      toDate: dayjs(selectedSecondDate).toDate(),
    };
  }, [
    entityType,
    selectedAgent,
    contractTypeOption,
    timePeriod,
    selectedFirstDate,
    selectedSecondDate,
    partnershipYearPeriod,
  ]);

  const {
    agencyDashboardStatistics: agencyDashboardStatisticsData,
    isPending: loadRexStatsPending,
    isError: loadRexStatsError,
  } = useLoadRexStatsByTypeByDate(updatedFilters);

  const isCustomDateSelected =
    timePeriod.value == StatisticsForEntityRequestTimePeriod.Custom.toString();

  const areBothLoaded = !loadRexStatsPending || !partnershipStatsPending;

  const title = useMemo(() => {
    if (!areBothLoaded) return 'Loading sales data';

    if (areBothLoaded && !(partnershipInfo || agencyDashboardStatisticsData)) {
      return 'No sales data to show';
    }

    switch (entityType.value) {
      case StatisticsForEntityRequestEntityType.IndividualOrTeam.toString():
        return (
          `Statistics for ${selectedAgent?.label ?? userAccount?.fullName}` ||
          'Individual/Team Dashboard'
        );
      case StatisticsForEntityRequestEntityType.Agency.toString():
        return `Statistics for ${agencyName}`;
      case StatisticsForEntityRequestEntityType.Partnership.toString():
        return `Partnership statistics for ${agencyName}`;
      default:
        return userAccount?.fullName || '';
    }
  }, [
    areBothLoaded,
    partnershipInfo,
    agencyDashboardStatisticsData,
    userAccount,
    agencyName,
  ]);

  const invalidateLoadRexStats =
    useInvalidateLoadRexStatsByTypeByDate(updatedFilters);

  const handleSaveGoals = async (saleGoals: SaleGoals) => {
    await saveGoals(
      entityType.value as StatisticsForEntityRequestEntityType,
      saleGoals
    );
    await invalidateLoadRexStats();
  };

  const modalContent = isMobileSize && (
    <AgxSpacer direction='vertical' fill gap='lg' padding='md'>
      <AgxDropdown
        id="entityType"
        placeholder={entityTypeOptions[0].label}
        selected={tempEntityType.value}
        onChange={value => {
          setTempEntityType(
            entityTypeOptions.find(x => x.value === value) ??
              entityTypeOptions[0]
          );
        }}
        options={entityTypeOptions}
        hideOptionalText
        noHeader
      />
      <AgxDropdown
        id="agents"
        placeholder={agents[0]?.label}
        selected={tempSelectedAgent?.value ?? ''}
        onChange={value => {
          const selected = agents.find(agents => agents.value === value);
          if (selected) {
            setTempSelectedAgent(selected);
          }
        }}
        options={agents}
        hideOptionalText
        noHeader
      />
      <AgxDropdown
        id="contractDate"
        label="Contract Date"
        placeholder={contractTypeOptions[0].label}
        selected={tempContractTypeOption?.value ?? ''}
        onChange={value => {
          const selected = contractTypeOptions.find(opt => opt.value === value);
          if (selected) {
            setTempContractTypeOption(selected);
          }
        }}
        options={contractTypeOptions}
        hideOptionalText
        noHeader
      />
      <AgxDropdown
        id="timePeriods"
        placeholder={TimePeriods[0].label}
        selected={tempTimePeriod?.value ?? ''}
        onChange={value => {
          setTempTimePeriod(
            TimePeriods.find(x => x.value === value) ?? TimePeriods[0]
          );
        }}
        options={TimePeriods}
        hideOptionalText
        noHeader
        customValueKey={TimePeriods[4].value}
        custom={
          <AgxColumn largeGap fill>
            <AgxTextInput
              id="start-date"
              size="large"
              type="date"
              label="Start date"
              defaultValue={tempStartDate}
              onInputValueChange={date => {
                setTempStartDate(date.value);
              }}
              placeholder="Select start date"
              hideOptionalText
              stretch
            />
            <AgxTextInput
              id="end-date"
              size="large"
              type="date"
              label="End date"
              defaultValue={tempEndDate}
              onInputValueChange={date => {
                setTempEndDate(date.value);
              }}
              placeholder="Select end date"
              hideOptionalText
              stretch
            />
          </AgxColumn>
        }
      />
    </AgxSpacer>
  );

  const filterTags = [
    entityType.value,
    selectedAgent?.value,
    contractTypeOption.value,
    timePeriod.value,
  ].filter(Boolean).length;

  return (
    <AgxColumn fill extraClasses="reportingDashboard">
      <div className="reportTitle">
        <AgxRow extraClasses="reportTitleHeading" centered>
          <AgxHeader size={2} inverse>
            Dashboard
          </AgxHeader>
        </AgxRow>
        {isMobileSize && (
          <AgxRow wrap>
            <AgxButton
              id="filterButton"
              size="large"
              variant="primary"
              text="Filter"
              tags={[filterTags > 0 ? `${filterTags}` : '']}
              onClick={() => setIsFilterOpen(true)}
            />
          </AgxRow>
        )}
        {!isMobileSize ? (
          <AgxRow justifyCenter>
            <AgxSelect
              id="entityTypeSelector"
              defaultValue={
                entityTypeOptions.find(x => x.value === entityType.value) ??
                entityTypeOptions[0]
              }
              options={entityTypeOptions}
              onValueChanged={({ value }) =>
                setEntityType(
                  entityTypeOptions.find(x => x.value === value) ??
                    entityTypeOptions[0]
                )
              }
              noHeader
              inline
              extraClasses="entityTypeSelector"
            />
            {userAccount?.roles?.includes(AgentRole.Director) &&
              !isAgentsInAgencyPending &&
              selectedAgent &&
              entityType.value ===
                StatisticsForEntityRequestEntityType.IndividualOrTeam.toString() &&
              agents && (
                <AgxSelect
                  id="directorAgencyMembersView"
                  options={agents}
                  defaultValue={selectedAgent}
                  onValueChanged={({ value }) => {
                    const selected = agents.find(
                      agents => agents.value === value
                    );
                    if (selected) {
                      setSelectedAgent(selected);
                    }
                  }}
                  noHeader
                  inline
                />
              )}
            {!showPartnershipInfo && (
              <>
                <AgxSelect
                  id="contractType"
                  options={contractTypeOptions}
                  defaultValue={contractTypeOption}
                  onValueChanged={({ value }) => {
                    const selected = contractTypeOptions.find(
                      opt => opt.value === value
                    );
                    if (selected) {
                      setContractTypeOption(selected);
                    }
                  }}
                  noHeader
                  inline
                />
                <AgxSelect
                  id="timePeriodSelector"
                  defaultValue={
                    TimePeriods.find(x => x.value === timePeriod.value) ??
                    TimePeriods[0]
                  }
                  options={TimePeriods}
                  onValueChanged={({ value }) => {
                    setTimePeriod(
                      TimePeriods.find(x => x.value === value) ?? TimePeriods[0]
                    );
                  }}
                  noHeader
                  inline
                  extraClasses={'rightAligned'}
                />
              </>
            )}
            {showPartnershipInfo && (
              <AgxSelect
                id="partnershipYearSelector"
                defaultValue={
                  PartnershipYearOptions.find(
                    x => x.value === partnershipYearPeriod.value
                  ) ?? PartnershipYearOptions[0]
                }
                options={PartnershipYearOptions}
                onValueChanged={({ value }) =>
                  setPartnershipYearPeriod(
                    PartnershipYearOptions.find(x => x.value === value) ??
                      PartnershipYearOptions[0]
                  )
                }
                noHeader
                inline
              />
            )}
            {isCustomDateSelected && !showPartnershipInfo && (
              <AgxDateRangeSelector
                id="reporting-dashboard-date-range"
                defaultStartDate={selectedFirstDate}
                defaultEndDate={selectedSecondDate}
                onChange={({ startDate, endDate }) => {
                  setSelectedFirstDate(startDate);
                  setSelectedSecondDate(endDate);
                }}
              />
            )}
          </AgxRow>
        ) : (
          isFilterOpen && (
            <AgxModal
              type="wide"
              title="Filters"
              setIsDialogOpen={setIsFilterOpen}
              content={modalContent}
              variant="withoutDivider"
              actionButtonContainerStyle="withoutDivider"
              actionButtons={
                <AgxRow justifyCenter fill largeGap>
                  <AgxButton
                    id="cancel"
                    variant="hollow"
                    size="large"
                    text="Cancel"
                    onClick={() => setIsFilterOpen(false)}
                  />
                  <AgxButton
                    id="saveGoals"
                    size="large"
                    variant="primary"
                    text="Confirm"
                    onClick={() => {
                      setEntityType(tempEntityType);
                      setSelectedAgent(tempSelectedAgent);
                      setContractTypeOption(tempContractTypeOption);
                      setTimePeriod(tempTimePeriod);
                      setSelectedFirstDate(tempStartDate);
                      setSelectedSecondDate(tempEndDate);
                      setIsFilterOpen(false);
                    }}
                  />
                </AgxRow>
              }
            />
          )
        )}
      </div>
      <div className="reportMainBody">
        {isDialogOpen && (
          <GoalsModal
            saveGoals={handleSaveGoals}
            goals={goals}
            setIsDialogOpen={setIsDialogOpen}
          />
        )}
        {!showPartnershipInfo && (
          <Leaderboard
            loading={leaderboardPending}
            leaderboardStats={leaderboardStatistics}
            timePeriod={timePeriod.value}
            entityType={
              entityType.value as StatisticsForEntityRequestEntityType
            }
            hideLeaderboard={leaderboardStatistics?.hideLeaderboard ?? false}
            customDateRange={
              isCustomDateSelected
                ? {
                    startDate: selectedFirstDate,
                    endDate: selectedSecondDate,
                  }
                : undefined
            }
          />
        )}
        {showPartnershipInfo && (
          <UpcomingSettlements
            loading={partnershipStatsPending}
            partnershipInfo={partnershipInfo}
          />
        )}
        {partnershipStatsError &&
          entityType.value !==
            StatisticsForEntityRequestEntityType.IndividualOrTeam.toString() && (
            <AgxColumn centered extraClasses="reportCardSection">
              <AgxRow spaceBetween extraClasses="reportCardTitle" fill>
                <AgxHeader size={5}>Error on data retrieval</AgxHeader>
              </AgxRow>
              <AgxRow fill>
                <AgxHeader size={3}>{partnershipStatsError}</AgxHeader>
              </AgxRow>
            </AgxColumn>
          )}
        <AgxColumn centered extraClasses="reportCardSection">
          <AgxRow extraClasses="reportCardTitle" fill spaceBetween>
            <AgxHeader size={5}>{title}</AgxHeader>
            <AgxButton
              id="editGoals"
              size="small"
              variant="dark"
              onClick={() => setIsDialogOpen(!isDialogOpen)}
              text="Edit Goals"
            />
          </AgxRow>
          {!partnershipStatsError &&
            showPartnershipInfo &&
            !agencyInfo.info.customizedPartnershipAgreement && (
              <>
                <AgxPartnershipIndicator
                  currentCommission={
                    partnershipInfo?.stats?.statistics?.grossCommissionIncome ??
                    0
                  }
                  partnershipLevel={
                    (partnershipInfo?.partnershipLevel as unknown as PartnershipDetailsPartnershipLevel) ??
                    PartnershipDetailsPartnershipLevel.Bronze
                  }
                  reviewDate={partnershipInfo?.reviewDate ?? new Date()}
                  commissionTargetAdjustment={
                    partnershipInfo?.commissionTargetAdjustment ?? 0
                  }
                />
              </>
            )}
          {!loadRexStatsError && (
            <AgxEntityStatistics
              loading={loadRexStatsPending}
              personalStatistics={agencyDashboardStatisticsData}
              agencyGoals={agencyGoals}
            />
          )}
        </AgxColumn>
      </div>
    </AgxColumn>
  );
};

export default ReportingDashboard;
